import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Eye, EyeOff, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TERMS_VERSION = "1.0";
const PRIVACY_VERSION = "1.0";

export default function PrimerAccesoPage() {
  const navigate = useNavigate();
  const { user, token, fetchUser } = useAuth();

  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [strength, setStrength] = useState(null);

  // Live feedback de fortaleza
  const checkStrength = async (pwd) => {
    setNewPass(pwd);
    if (pwd.length < 4) {
      setStrength(null);
      return;
    }
    try {
      const r = await axios.post(`${API}/auth/password/validate`, { password: pwd }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStrength(r.data);
    } catch {
      setStrength(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Debe aceptar los Términos y Condiciones y la Política de Privacidad");
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("La nueva contraseña y su confirmación no coinciden");
      return;
    }
    if (newPass === "siret") {
      toast.error("La nueva contraseña no puede ser igual a la temporal");
      return;
    }

    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/first-time-setup`, {
        new_password: newPass,
        new_password_confirm: confirmPass,
        terms_accepted: true,
        terms_version: TERMS_VERSION,
        privacy_version: PRIVACY_VERSION,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Persistir nuevo token (issued tras setup completo)
      if (r.data?.access_token) {
        sessionStorage.setItem("access_token", r.data.access_token);
        if (r.data.refresh_token) sessionStorage.setItem("refresh_token", r.data.refresh_token);
      }
      toast.success("¡Cuenta activada! Redirigiendo al portal...");
      if (fetchUser) await fetchUser();
      setTimeout(() => navigate("/portal/dashboard", { replace: true }), 1200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Error al completar el primer acceso");
      toast.error(msg);
      if (typeof detail === "object" && Array.isArray(detail?.errors)) {
        detail.errors.forEach((e) => toast.warning(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const score = strength?.score ?? 0;
  const scoreColors = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-emerald-500", "bg-green-600"];
  const scoreLabels = ["Muy débil", "Débil", "Regular", "Buena", "Excelente"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4" data-testid="primer-acceso-page">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-500 text-white px-8 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-yellow-400 flex items-center justify-center text-emerald-900">
              <ShieldCheck size={22} />
            </div>
            <span className="text-xs tracking-[3px] text-emerald-100 uppercase font-semibold">Bienvenido a SIRET</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight">
            Activación de tu cuenta · Primer acceso
          </h1>
          <p className="mt-2 text-emerald-50 text-sm">
            Hola <strong>{user?.nombre} {user?.apellido}</strong>, tu cuenta fue creada por un funcionario municipal con una clave temporal. Para tu seguridad, debes completar 2 pasos antes de continuar:
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
          {/* Aviso */}
          <div className="flex items-start gap-3 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3" data-testid="primer-acceso-warning">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-xs text-amber-800 leading-snug">
              La contraseña temporal <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono">siret</code> no cumple las políticas de seguridad. Debes elegir una contraseña fuerte y aceptar los términos legales para activar tu acceso al portal.
            </div>
          </div>

          {/* Paso 1: Términos */}
          <section className="space-y-2" data-testid="step-terms">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs">1</span>
              Aceptación de Términos y Privacidad
            </h2>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer"
                  data-testid="primer-acceso-accept-terms"
                />
                <span className="text-xs text-slate-700 leading-snug">
                  He leído y acepto los{" "}
                  <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold underline" data-testid="primer-acceso-link-terminos">
                    Términos y Condiciones de Uso
                  </a>{" "}
                  (v{TERMS_VERSION}) y la{" "}
                  <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold underline" data-testid="primer-acceso-link-privacidad">
                    Política de Privacidad
                  </a>{" "}
                  (v{PRIVACY_VERSION}) de SIRET. Esta aceptación quedará registrada en el audit log forense con timestamp UTC y hash criptográfico de las versiones aceptadas.
                </span>
              </label>
            </div>
          </section>

          {/* Paso 2: Cambio de clave */}
          <section className="space-y-3" data-testid="step-password">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs">2</span>
              Nueva contraseña segura
            </h2>

            <div className="space-y-1.5">
              <Label htmlFor="new-pass" className="text-slate-700 text-sm">Nueva contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="new-pass"
                  data-testid="primer-acceso-new-password"
                  type={showPass ? "text" : "password"}
                  placeholder="Mínimo 8 caracteres"
                  className="pl-10 pr-10"
                  value={newPass}
                  onChange={(e) => checkStrength(e.target.value)}
                  required
                  minLength={8}
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowPass(!showPass)}>
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {strength && (
                <div className="space-y-1" data-testid="primer-acceso-strength">
                  <div className="flex gap-1 h-1.5">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`flex-1 rounded-full ${i < score ? scoreColors[score] : "bg-slate-200"}`} />
                    ))}
                  </div>
                  <div className="text-xs text-slate-600 flex items-center justify-between">
                    <span>Fortaleza: <strong>{scoreLabels[score]}</strong></span>
                    {strength.ok && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} /> Cumple política</span>}
                  </div>
                  {strength.errors?.length > 0 && (
                    <ul className="text-xs text-red-600 list-disc pl-4 space-y-0.5">
                      {strength.errors.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-pass" className="text-slate-700 text-sm">Confirmar nueva contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="confirm-pass"
                  data-testid="primer-acceso-confirm-password"
                  type={showPass ? "text" : "password"}
                  placeholder="Repita la nueva contraseña"
                  className="pl-10"
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              {confirmPass && newPass !== confirmPass && (
                <p className="text-xs text-red-600">Las contraseñas no coinciden</p>
              )}
            </div>
          </section>

          <Button
            type="submit"
            className="w-full text-white bg-emerald-600 hover:bg-emerald-700 mt-4"
            data-testid="primer-acceso-submit"
            disabled={loading || !acceptedTerms || !newPass || newPass !== confirmPass || (strength && !strength.ok)}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                Activando cuenta...
              </span>
            ) : "Activar mi cuenta"}
          </Button>

          <p className="text-center text-xs text-slate-500 mt-2">
            No podrás acceder al portal hasta completar ambos pasos. Esta acción quedará registrada en el audit log forense del sistema.
          </p>
        </form>
      </div>
    </div>
  );
}
